/**
 * Casamento no tempo entre os quadros do loop e os do rastreio, e o frescor do
 * índice — funções puras (testadas em `tracking.test.ts`).
 *
 * A IMAGEM DO SATÉLITE É A OBSERVAÇÃO, e manda: o rastreio e a previsão só
 * aparecem quando são do MESMO horário da imagem exibida. O rastreio sai da
 * mesma varredura do disco completo que as imagens do STAR (:00, :10, :20…) e
 * leva o mesmo carimbo, então em regime o casamento é exato. Diferente da
 * fumaça, aqui não há vizinho: um sistema anda 10–20 km em 10 min, e um
 * contorno de outro horário sobre a nuvem errada engana. Sem rastreio daquele
 * horário (a imagem chegou antes, ou a rodada falhou), fica só a imagem.
 */
import { MINUTE_MS } from '../lib/time';
import { varreduraParaQuadro } from '../radar/tempo';
import type { QuadroRastreio } from './api';

/** Menos que a cadência (10 min): só cobre carimbos que diferem por arredondamento. */
export const TOLERANCIA_MS = 4 * MINUTE_MS;

/** O quadro do rastreio do instante `t`, ou null. */
export function quadroParaInstante(quadros: readonly QuadroRastreio[], tempos: readonly number[], t: number | null): QuadroRastreio | null {
  if (t === null || !quadros.length) return null;
  const i = varreduraParaQuadro(tempos, t, TOLERANCIA_MS);
  return i >= 0 ? quadros[i] : null;
}

/**
 * A NOAA entrega o canal 13 ~8 min depois da varredura e a rodada leva mais
 * alguns: o normal é o mais novo ter 10–25 min. Passando de 60, avisa.
 */
export const ATRASO_AVISO_MIN = 60;

function quanto(min: number): string {
  return min >= 120 ? `${Math.floor(min / 60)} h` : `${Math.round(min)} min`;
}

/** Aviso sobre o quadro mais novo publicado, ou null quando está em dia. */
export function avisoDeFrescor(ultimo: number | null, agora: number = Date.now()): string | null {
  if (ultimo === null) return null;
  const min = Math.max(0, (agora - ultimo) / MINUTE_MS);
  return min >= ATRASO_AVISO_MIN ? `Rastreio sem quadro novo há ${quanto(min)}. As imagens mais recentes aparecem sem ele.` : null;
}

/**
 * A antecedência oferecida mais próxima de `min` (ou `min`, sem lista). Zero é
 * a observação (nenhum polígono previsto) e vale sempre.
 */
export function antecedenciaValida(min: number, oferecidas: readonly number[]): number {
  if (min <= 0) return 0;
  if (!oferecidas.length) return min;
  return oferecidas.reduce((melhor, a) => (Math.abs(a - min) < Math.abs(melhor - min) ? a : melhor), oferecidas[0]);
}

/** A próxima antecedência, voltando à primeira depois da última. */
export function proximaAntecedencia(min: number, oferecidas: readonly number[]): number {
  if (!oferecidas.length) return min;
  const i = oferecidas.indexOf(antecedenciaValida(min, oferecidas));
  return oferecidas[(i + 1) % oferecidas.length];
}

/** As antecedências (`h`, min) que um arquivo de previsão traz, crescentes e sem repetir. */
export function antecedenciasDoArquivo(hs: Iterable<number | null | undefined>): number[] {
  const vistas = new Set<number>();
  for (const h of hs) if (typeof h === 'number' && Number.isFinite(h) && h > 0) vistas.add(h);
  return [...vistas].sort((a, b) => a - b);
}

export interface AntecedenciasNoQuadro {
  /** Os botões do seletor, sempre os mesmos de um quadro para o outro. */
  passos: number[];
  /** As que o quadro exibido tem de fato: as demais ficam desabilitadas. */
  disponiveis: number[];
}

/**
 * O índice diz o passo da previsão de TODOS os quadros, mas cada arquivo é
 * imutável e guarda o passo de quando foi gerado: na troca de 10 min/1 h para
 * 30 min/3 h (29/09/2026) os arquivos antigos ficaram com `h` de 10 a 60. Quem
 * manda é o arquivo do quadro exibido (`noArquivo`; null = ainda não chegou).
 * - arquivo com parte das antecedências do índice: o seletor é o do índice, e
 *   só as que o arquivo tem ficam disponíveis;
 * - arquivo sem nenhuma em comum: o seletor passa a ser o do arquivo;
 * - arquivo vazio (nenhum sistema previsto) ou ainda baixando: o do índice.
 */
export function antecedenciasNoQuadro(oferecidas: readonly number[], noArquivo: readonly number[] | null): AntecedenciasNoQuadro {
  const doIndice = oferecidas.filter((a) => a > 0);
  if (!noArquivo?.length) return { passos: doIndice, disponiveis: doIndice };
  const comuns = doIndice.filter((a) => noArquivo.includes(a));
  if (comuns.length) return { passos: doIndice, disponiveis: comuns };
  return { passos: [...noArquivo], disponiveis: [...noArquivo] };
}

/** Rótulo curto de uma antecedência: "+30", "+1 h", "+1h30" (a partir de 60 min, em horas). Zero é a observação. */
export function rotuloAntecedencia(min: number, opcoes: { unidade?: boolean } = {}): string {
  if (min <= 0) return '0';
  if (min < 60) return opcoes.unidade ? `+${min} min` : `+${min}`;
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  return m === 0 ? `+${h} h` : `+${h}h${String(m).padStart(2, '0')}`;
}

/** A antecedência por extenso, para o leitor de tela: "30 minutos", "1 hora e 30 minutos". */
export function antecedenciaPorExtenso(min: number): string {
  if (min <= 0) return 'agora';
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  const horas = h === 0 ? '' : h === 1 ? '1 hora' : `${h} horas`;
  const minutos = m === 0 ? '' : m === 1 ? '1 minuto' : `${m} minutos`;
  return horas && minutos ? `${horas} e ${minutos}` : horas || minutos;
}

/** Por que uma antecedência não existe no quadro exibido, em linguagem de usuário. */
export function motivoDaFalta(min: number, disponiveis: readonly number[]): string {
  const ultima = disponiveis[disponiveis.length - 1];
  const pedida = rotuloAntecedencia(min, { unidade: true });
  if (ultima === undefined) return `Esta imagem não tem previsão de ${pedida}.`;
  return min > ultima
    ? `Esta imagem não tem previsão de ${pedida}: a previsão dela vai só até ${rotuloAntecedencia(ultima, { unidade: true })}.`
    : `Esta imagem não tem previsão de ${pedida}: a previsão dela tem ${disponiveis.map((a) => rotuloAntecedencia(a, { unidade: true })).join(', ')}.`;
}

/** Aviso quando a antecedência escolhida não existe no quadro exibido, ou null. */
export function avisoDeAntecedencia(escolhida: number, naTela: number, disponiveis: readonly number[]): string | null {
  // Na observação ("agora") não há previsão na tela: nada a explicar.
  if (escolhida <= 0 || naTela <= 0 || escolhida === naTela || disponiveis.includes(escolhida)) return null;
  const ultima = disponiveis[disponiveis.length - 1];
  if (ultima === undefined) return null;
  const alcance = `a previsão desta imagem vai até ${rotuloAntecedencia(ultima, { unidade: true })}`;
  return `Esta imagem não tem previsão de ${rotuloAntecedencia(escolhida, { unidade: true })} (${alcance}): o mapa mostra ${rotuloAntecedencia(naTela, { unidade: true })}.`;
}

/**
 * A previsão no título da exportação. A antecedência desenhada é a do quadro
 * na tela (a mesma do aviso sobre o mapa); se ela não é a escolhida, o título
 * diz as duas — num loop que mistura arquivos antigos e novos cada quadro
 * desenha a que tem.
 */
export function previsaoNoTitulo(escolhida: number, naTela: number): string {
  if (naTela <= 0) return 'previsão de nuvens';
  const desenhada = rotuloAntecedencia(naTela, { unidade: true });
  if (escolhida <= 0 || escolhida === naTela) return `previsão de nuvens (${desenhada})`;
  return `previsão de nuvens (${desenhada}; ${rotuloAntecedencia(escolhida, { unidade: true })} onde houver)`;
}

// ---------------------------------------------------------------------------
// Fluxo da previsão
// ---------------------------------------------------------------------------
//
// A previsão é uma extrapolação a partir de UMA observação: com ela ligada o
// loop principal para, e o que anda é a antecedência — a observação (0), cada
// antecedência do quadro (+30 min → … → +3 h), uma pausa no fim, e de novo.

/**
 * Tempo em cada passo do fluxo (ms). Eram 900 com passos de 10 min; com 30 min
 * o contorno salta três vezes mais longe a cada passo e o olho precisa de mais
 * tempo para achar o sistema de novo. O ciclo inteiro fica em ~10 s.
 */
export const PASSO_FLUXO_MS = 1200;
/** Pausa extra no último passo, antes de voltar à observação (ms). */
export const PAUSA_FIM_FLUXO_MS = 1500;

/** Os passos do fluxo: a observação (0) e as antecedências oferecidas. */
export function passosDoFluxo(oferecidas: readonly number[]): number[] {
  return [0, ...oferecidas.filter((a) => a > 0)];
}

/** O passo seguinte a `atual`, voltando à observação depois do último. */
export function proximoPasso(atual: number, oferecidas: readonly number[]): number {
  const passos = passosDoFluxo(oferecidas);
  const i = passos.indexOf(antecedenciaValida(atual, oferecidas));
  return passos[(i + 1) % passos.length];
}

/** Quanto o fluxo fica no passo `atual` (ms): o último espera mais. */
export function esperaDoPasso(atual: number, oferecidas: readonly number[]): number {
  const passos = passosDoFluxo(oferecidas);
  const ultimo = passos.length > 1 && antecedenciaValida(atual, oferecidas) === passos[passos.length - 1];
  return PASSO_FLUXO_MS + (ultimo ? PAUSA_FIM_FLUXO_MS : 0);
}

/**
 * O quadro mais novo do loop que tem previsão: a origem da zona de previsão
 * da linha do tempo. Índice em `frames`, ou −1 quando nenhum tem. Com o passo
 * do loop de 30 min ou 1 h é o mais novo DESTES quadros, não o do índice.
 */
export function quadroMaisNovoComPrevisao(frames: readonly number[], quadros: readonly QuadroRastreio[]): number {
  if (!quadros.length) return -1;
  const tempos = quadros.map((q) => q.t);
  for (let i = frames.length - 1; i >= 0; i--) if (quadroParaInstante(quadros, tempos, frames[i])?.prev === true) return i;
  return -1;
}

/**
 * De que quadro do loop a previsão parte: o exibido, se ele tem previsão;
 * senão o mais novo do loop que tem. Devolve o índice em `frames`, ou −1
 * quando nenhum quadro do loop tem previsão.
 */
export function quadroDeOrigem(frames: readonly number[], quadros: readonly QuadroRastreio[], atual: number): number {
  const tempos = quadros.map((q) => q.t);
  if (quadroParaInstante(quadros, tempos, frames[atual] ?? null)?.prev === true) return atual;
  return quadroMaisNovoComPrevisao(frames, quadros);
}
